import React from "react";
import EmojiPicker from "@/components/EmojiPicker";

/**
 * ReactionRow — render grouped reaction chips for a single comment/message.
 *
 * Props:
 *   - reactions: Array<{ emoji: string, user_id: string }>
 *   - currentUserId: id of the logged-in user (used to highlight their own picks)
 *   - onToggle(emoji): called when the user clicks an existing chip or picks a new emoji.
 *       The backend handles toggle semantics (clicking your own reaction removes it).
 *   - size: "sm" (compact, for replies/chat) | "md" (default)
 *   - testid: data-testid prefix
 *   - hidePickerWhenEmpty: when true and there are zero reactions, only the picker shows
 */
export default function ReactionRow({
  reactions = [],
  currentUserId,
  onToggle,
  size = "md",
  testid = "reactions",
}) {
  // Group by emoji while preserving insertion order
  const groups = [];
  const map = new Map();
  for (const r of reactions || []) {
    if (!map.has(r.emoji)) {
      const g = { emoji: r.emoji, count: 0, mine: false };
      map.set(r.emoji, g);
      groups.push(g);
    }
    const g = map.get(r.emoji);
    g.count += 1;
    if (r.user_id === currentUserId) g.mine = true;
  }

  const chipBase =
    size === "sm"
      ? "inline-flex items-center gap-1 h-6 px-2 rounded-full text-[11px] border transition-all active:scale-95"
      : "inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs border transition-all active:scale-95";

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid={`${testid}-row`}
    >
      {groups.map((g) => (
        <button
          key={g.emoji}
          type="button"
          onClick={() => onToggle && onToggle(g.emoji)}
          className={`${chipBase} ${
            g.mine
              ? "bg-[hsl(var(--accent))]/15 border-[hsl(var(--accent))]/50 text-[hsl(var(--accent))] font-semibold"
              : "bg-[hsl(var(--secondary))] border-transparent text-foreground hover:bg-[hsl(var(--secondary))]/70"
          }`}
          aria-label={`React ${g.emoji} (${g.count})`}
          title={g.mine ? `You reacted with ${g.emoji} — click to remove` : `React with ${g.emoji}`}
          data-testid={`${testid}-chip-${g.emoji}`}
        >
          <span className={size === "sm" ? "text-sm leading-none" : "text-base leading-none"}>{g.emoji}</span>
          <span>{g.count}</span>
        </button>
      ))}
      <EmojiPicker
        asReaction
        onSelect={(e) => onToggle && onToggle(e)}
        testid={`${testid}-picker`}
      />
    </div>
  );
}
